import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BETA_TERMS_VERSION } from "@/lib/betaTerms";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/States";

export function ReportDialog({ open, onOpenChange }) {
  const loc = useLocation();
  const [msg, setMsg] = useState("");
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const pick = (f) => { if (f && f.type.startsWith("image/")) setShot(f); else if (f) toast.error("Attach an image (PNG or JPG)"); };
  const onPaste = (e) => { const it = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/")); if (it) { pick(it.getAsFile()); e.preventDefault(); } };
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData();
    fd.append("message", msg.trim()); fd.append("page", window.location.href); fd.append("ua", navigator.userAgent);
    if (shot) fd.append("file", shot, shot.name || "screenshot.png");
    try { await api.post("/beta/report", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Thanks — it's on its way to the E.L.M team"); setMsg(""); setShot(null); onOpenChange(false); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="section-title">Report a problem</DialogTitle><DialogDescription>Tell us what happened. We'll include this page ({loc.pathname}) and your workspace automatically.</DialogDescription></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="rep-msg">What went wrong, or what did you expect?</Label><Textarea id="rep-msg" rows={4} required minLength={3} maxLength={3000} value={msg} onChange={(e) => setMsg(e.target.value)} onPaste={onPaste} placeholder="e.g. Saving the estimate spun forever after I added a line… (you can paste a screenshot here)" data-testid="report-message-input" /></div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="report-file-input" />
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="report-attach-button"><ImagePlus className="mr-1.5 h-3.5 w-3.5" />{shot ? "Change screenshot" : "Attach screenshot"}</Button>
            {shot && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid="report-attachment">{shot.name || "Pasted image"} · {Math.round(shot.size / 1024)} KB<button type="button" onClick={() => setShot(null)} aria-label="Remove" data-testid="report-attachment-remove"><X className="h-3 w-3" /></button></span>}
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={busy || msg.trim().length < 3} data-testid="report-submit">{busy ? <Spinner /> : "Send report"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function BetaBanner() {
  const { beta } = useAuth();
  const [open, setOpen] = useState(false);
  if (!beta) return null;
  return (
    <>
      <div className="flex items-center justify-center gap-2 border-b bg-foreground px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-background" data-testid="beta-banner">
        <span>Private beta · v{BETA_TERMS_VERSION}</span>
        <span aria-hidden="true" className="opacity-40">·</span>
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 underline-offset-4 hover:underline" data-testid="beta-report-button"><Bug className="h-3 w-3" />Report a problem</button>
      </div>
      <ReportDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
